import { z } from 'zod';

/**
 * 프로토콜 JSON 스키마.
 *
 * protocols/*.protocol.json 은 출처 주석이 붙은 형태다 — 값이 `{ value, source, ... }` 로 감싸져 있다.
 * 여기서는 감싼 형태와 날것 둘 다 받아 `resolve*` 로 평탄화한다.
 * 근거 없는 값이 스키마를 통과하지 못하게 하는 것이 이 파일의 목적이다.
 */

const annotated = <T extends z.ZodTypeAny>(inner: T) =>
  z.union([inner, z.object({ value: inner }).passthrough().transform((o) => o.value as z.infer<T>)]);

const ms = annotated(z.number().int().positive());

export const dotProbeProtocolSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  version: z.string(),
  task: z.literal('CBM_A'),
  signConvention: z.literal('vigilance-positive'),

  timing: z.object({
    fixationMs: ms,
    cueSoaMs: ms,
    probeMs: annotated(z.union([z.literal('until-response'), z.number().int().positive()])),
    itiMs: ms,
    feedbackMs: ms.optional(),
  }).passthrough(),

  trials: z.object({
    training: z.object({
      total: z.number().int().positive(),
      composition: z.object({ threatNeutral: z.number().int().nonnegative(), neutralNeutral: z.number().int().nonnegative() }),
    }).passthrough(),
    assessment: z.object({
      total: z.number().int().positive(),
      composition: z.object({ congruent: z.number().int(), incongruent: z.number().int(), neutralNeutral: z.number().int() }),
      contingency: z.number().min(0).max(1),
    }).passthrough(),
    practice: z.object({ total: z.number().int().nonnegative(), feedback: z.boolean() }).passthrough(),
  }),

  contingency: z.object({
    training: z.number().min(0).max(1),
    sham: z.number().min(0).max(1),
    minimumAllowed: z.number().min(0).max(1),
  }).passthrough()
    // Vrijsen et al. 2024 각주 3: 모든 긍정적 시험은 100% 또는 95%. 87.5% 였던 Spruyt 2025 는 전면 null.
    .refine((c) => c.training >= c.minimumAllowed, {
      message: '훈련 우연성이 minimumAllowed 미만입니다. 90% 미만 프로토콜은 문헌상 실패했습니다 (Vrijsen 2024; Spruyt 2025).',
    }),

  layout: z.object({
    arrangement: z.enum(['vertical', 'horizontal']),
    geometry: z.object({
      stimulusHeightMm: z.number().positive(),
      separationMm: z.number().positive(),
      targetVisualAngleDeg: z.number().positive().optional(),
    }).passthrough(),
  }).passthrough(),

  stimuli: z.object({
    modality: z.enum(['words', 'faces']),
    pairs: z.number().int().positive(),
  }).passthrough(),

  preprocessing: z.object({
    absoluteBoundsMs: z.tuple([z.number(), z.number()]),
    dropFirstNPerBlock: z.number().int().nonnegative(),
  }).passthrough(),
}).passthrough();

export type DotProbeProtocol = z.infer<typeof dotProbeProtocolSchema>;

/** 엔진·생성기가 실제로 쓰는 평탄한 설정 */
export interface DotProbeConfig {
  id: string;
  version: string;
  fixationMs: number;
  cueSoaMs: number;
  probeTimeoutMs: number;
  itiMs: number;
  feedbackMs: number;
  arrangement: 'vertical' | 'horizontal';
  stimulusHeightMm: number;
  separationMm: number;
  training: { total: number; threatNeutral: number; neutralNeutral: number; contingency: number };
  sham: { contingency: number };
  assessment: { congruent: number; incongruent: number; neutralNeutral: number };
  practice: { total: number; feedback: boolean };
}

export function resolveDotProbeConfig(raw: unknown): DotProbeConfig {
  const p = dotProbeProtocolSchema.parse(raw);
  const probe = p.timing.probeMs;
  // 'until-response' 일 때의 상한은 주석 객체의 timeoutMs 에 있다. 변환 전 원본에서 읽는다.
  const rawTimeout = (raw as { timing?: { probeMs?: { timeoutMs?: unknown } } })?.timing?.probeMs?.timeoutMs;
  const probeTimeoutMs = probe === 'until-response'
    ? (typeof rawTimeout === 'number' ? rawTimeout : 2000)
    : probe;
  return {
    id: p.id,
    version: p.version,
    fixationMs: p.timing.fixationMs,
    cueSoaMs: p.timing.cueSoaMs,
    probeTimeoutMs,
    itiMs: p.timing.itiMs,
    feedbackMs: p.timing.feedbackMs ?? 500,
    arrangement: p.layout.arrangement,
    stimulusHeightMm: p.layout.geometry.stimulusHeightMm,
    separationMm: p.layout.geometry.separationMm,
    training: {
      total: p.trials.training.total,
      threatNeutral: p.trials.training.composition.threatNeutral,
      neutralNeutral: p.trials.training.composition.neutralNeutral,
      contingency: p.contingency.training,
    },
    sham: { contingency: p.contingency.sham },
    assessment: { ...p.trials.assessment.composition },
    practice: { total: p.trials.practice.total, feedback: p.trials.practice.feedback },
  };
}
